import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BBBUser, RoomtransferAudit } from '@reflact/kick';
import { RagDatasupplyFrontendService, RagDatasupplyTableQuicklookAsideComponent, RagDatasupplyTypes } from '@reflact/rag-datasupply';
import { removeFromArray } from '@reflact/tsutil';
import * as dayjs from 'dayjs';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import * as XLSX from 'xlsx';
import { AuthService } from '../shared/AuthService';
import { TransferProtocol, UserService } from './user.service';

export type TransferProtocolExport = {
  _id: string,
  fromUserName: string,
  toUserName: string,
  transferDate: string,
  transferByName: string,
  room: string
}

@Component({
  selector: 'app-user-admin',
  templateUrl: './user-admin.component.html',
  styles: [],
})
export class UserAdminComponent {
  @ViewChild("table") private table: RagDatasupplyTableQuicklookAsideComponent<BBBUser>
  public dayjs = dayjs;
  public users: BBBUser[] = [];
  public modalRef?: BsModalRef;
  public newUser: { name: string; email: string; isAdmin: boolean } = {
    name: '',
    email: '',
    isAdmin: false,
  };
  public inviteOnCreate: boolean = false;
  protected selectedUserForRoomTransfer: string = "";
  protected shouldRoomTransferIncludeClearences: boolean = false;
  protected transferChanges: RoomtransferAudit[] = [];
  protected transferProtocols: TransferProtocol[] = [];

  public translatedLabels: RagDatasupplyTypes.TranslatedLabels[] = [
    { fieldName: "name", translated: $localize`:@@name:Name` },
    { fieldName: "email", translated: $localize`:@@email:E-Mail` }
  ]
  constructor(
    public authService: AuthService,
    public frontendService: RagDatasupplyFrontendService<BBBUser>,
    public userService: UserService,
    public modalService: BsModalService,
    public aRoute: ActivatedRoute,
  ) {
    aRoute.data.subscribe((d) => {
      this.users = d.users;
      this.transferProtocols = d.protocols;
      this.frontendService.fromArray(this.users);
    });
  }

  public async inviteUser(id) {
    await this.userService.inviteUser(id);
    this.modalRef.hide();
  }

  public async addUser() {
    const permissions = [];
    if (this.newUser.isAdmin) {
      permissions.push('admin');
    }
    const newUser = await this.userService.addUser({
      name: this.newUser.name,
      email: this.newUser.email,
      permissions,
    });
    if (this.inviteOnCreate) {
      await this.inviteUser(newUser._id);
    }
    this.users.push(newUser);
    this.newUser = { name: '', email: '', isAdmin: false };
  }

  public async editUser(
    _id: string,
    editedUser: { name?: string; email?: string; isAdmin?: boolean },
  ) {
    const permissions = this.users.find((u) => u._id == _id).permissions;
    if (editedUser.isAdmin != null) {
      if (editedUser.isAdmin === true) {
        permissions.push('admin');
        this.table.selectedObject.permissions.push('admin');
      }
      if (editedUser.isAdmin === false) {
        removeFromArray(permissions, (p) => { return p == 'admin' });
        removeFromArray(this.table.selectedObject.permissions, (p) => { return p == 'admin' });
      }
    }
    delete editedUser.isAdmin;
    await this.userService.editUser(_id, {
      name: editedUser.name,
      email: editedUser.email,
      permissions,
    });
  }

  public async deleteUser(id: string) {
    await this.userService.deleteUser(id);
    this.users = await this.userService.getAllPortalUser()
    this.table.selectedObject = undefined;
    this.frontendService.fromArray(this.users);
    this.modalRef.hide()
  }

  protected getUsersWithoutOwnUser(id: string) {
    return this.users.filter(r => id !== r._id);
  }

  protected getUserForId() {
    return this.users.find(u => u._id === this.selectedUserForRoomTransfer);
  }

  protected async preloadTransferChanges(fromUserId: string) {
    this.transferChanges = await this.userService.getTransferChanges(fromUserId, this.selectedUserForRoomTransfer, this.shouldRoomTransferIncludeClearences);
  }

  protected async transferRoomsToUser(fromUserId: string) {
    this.transferChanges = await this.userService.confirmTransferChanges(fromUserId, this.selectedUserForRoomTransfer, this.shouldRoomTransferIncludeClearences);
    this.transferProtocols = await this.userService.getTransferProtocols();
  }

  protected downloadAllTransfers() {
    const data: TransferProtocolExport[] = [];
    for (const protocol of this.transferProtocols) {
      for (const room of protocol.rooms) {
        data.push({
          _id: protocol._id,
          fromUserName: protocol.fromUserName,
          toUserName: protocol.toUserName,
          transferByName: protocol.transferByName,
          transferDate: protocol.transferDate,
          room: room.name
        });
      }
    }
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [
      { wch: data.reduce((w, r) => Math.max(w, r._id.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.fromUserName.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.toUserName.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.transferByName.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.transferDate.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.room.length), 10) }
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws);
    XLSX.writeFileXLSX(workbook, 'transfer_protocols_' + dayjs().format('YYYYMMDD_HHmm') + '.xlsx')
  }

  protected downloadSingleTransfers(id: string) {
    const protocol = this.transferProtocols.filter(t => t._id === id)[0];
    const data: TransferProtocolExport[] = [];
    for (const room of protocol.rooms) {
      data.push({
        _id: protocol._id,
        fromUserName: protocol.fromUserName,
        toUserName: protocol.toUserName,
        transferByName: protocol.transferByName,
        transferDate: protocol.transferDate,
        room: room.name
      });
    }
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [
      { wch: data.reduce((w, r) => Math.max(w, r._id.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.fromUserName.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.toUserName.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.transferByName.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.transferDate.length), 10) },
      { wch: data.reduce((w, r) => Math.max(w, r.room.length), 10) }
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws);
    XLSX.writeFileXLSX(workbook, protocol._id + 'transfer_protocol_' + dayjs().format('YYYYMMDD_HHmm') + '.xlsx')
  }

  protected getSingleTransferData() {
    return this.transferProtocols[this.modalService.config.initialState.transferIndex as number]
  }
}
